import { useMemo, useRef } from 'react'
import { useQuery } from 'react-query'

import { useIsFetchingNewPeriod } from '@features/expertReports'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import type { APIError } from '../../../utils'
import { fetchExpertTrialBalance, type FetchExpertTrialBalanceParams } from '../query-api'
import type { ExpertTrialBalanceReport } from '../types/expertTrialBalanceReport'

interface UseFetchExpertTrialBalanceResult {
  displayedSearch: string
  isError: boolean
  isFetchingNewPeriod: boolean
  isLoading: boolean
  report: ExpertTrialBalanceReport | undefined
}

export const useFetchExpertTrialBalance = ({
  compareCount,
  compareWith,
  hideZeroes,
  organizationId,
  period,
  search,
}: FetchExpertTrialBalanceParams): UseFetchExpertTrialBalanceResult => {
  const queryProps = useMemo<FetchExpertTrialBalanceParams>(
    () => ({ compareCount, compareWith, hideZeroes, organizationId, period, search }),
    [compareCount, compareWith, hideZeroes, organizationId, period, search],
  )

  const {
    data: reportData,
    isError,
    isIdle,
    isLoading,
    isPreviousData,
  } = useQuery<ExpertTrialBalanceReport, APIError>(
    [QueryKeys.ExpertTrialBalance, queryProps],
    () => fetchExpertTrialBalance(queryProps),
    { enabled: !!organizationId && !!period, keepPreviousData: true, staleTime: defaultStaleTime },
  )

  const lastSettledSearchRef = useRef(search ?? '')
  if (!isPreviousData) {
    lastSettledSearchRef.current = search ?? ''
  }

  const isFetchingNewPeriod = useIsFetchingNewPeriod(period, isPreviousData)

  return {
    displayedSearch: lastSettledSearchRef.current,
    isError,
    isFetchingNewPeriod: isFetchingNewPeriod && !isError,
    isLoading: isIdle || isLoading,
    report: isError && isFetchingNewPeriod ? undefined : reportData,
  }
}
